import { type FormEvent, useId, useState } from 'react';
import validator from 'validator';
import ErrorMessage from '../../components/form/ErrorMessage';
import { MEMBER_ROLES, type MemberInvite } from '../../service/membership';

type Props = {
  busy: boolean;
  /** Rejects when the invite failed; the screen shows the error and the form keeps its text. */
  onInvite(members: MemberInvite[]): Promise<unknown>;
};

const ROLE_LABELS: Record<string, string> = { member: 'Member', admin: 'Admin' };

/** Lowercased, de-duplicated addresses from text separated by newlines, commas, semicolons or spaces. */
export const parseEmailList = (value: string): string[] => {
  const seen = new Set<string>();
  for (const part of value.split(/[\s,;]+/)) {
    const email = part.trim().toLowerCase();
    if (email) seen.add(email);
  }
  return [...seen];
};

export default function InviteMembersForm({ busy, onInvite }: Props) {
  const id = useId();
  const [emails, setEmails] = useState('');
  const [role, setRole] = useState<string>('member');
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const list = parseEmailList(emails);
    if (list.length === 0) {
      setError('Enter at least one email address');
      return;
    }
    const invalid = list.filter((email) => !validator.isEmail(email));
    if (invalid.length > 0) {
      setError(`Enter a valid email address for: ${invalid.join(', ')}`);
      return;
    }
    setError(null);
    try {
      await onInvite(list.map((email) => ({ email, role })));
      setEmails('');
    } catch (_error) {
      // The screen reports the failure; keep the addresses so they can retry.
    }
  };

  return (
    <form onSubmit={submit} noValidate>
      <fieldset className="govuk-fieldset">
        <legend className="govuk-fieldset__legend govuk-fieldset__legend--s">People to invite</legend>
        <div className={`govuk-form-group${error ? ' govuk-form-group--error' : ''}`}>
          <label className="govuk-label" htmlFor={`${id}-emails`}>
            Email addresses
          </label>
          <div className="govuk-hint" id={`${id}-emails-hint`}>
            One per line, or separated by commas. Each person is invited and sees this service the
            next time they sign in.
          </div>
          {error && <ErrorMessage error={{ message: error }} />}
          <textarea
            id={`${id}-emails`}
            className={`govuk-textarea${error ? ' govuk-textarea--error' : ''}`}
            rows={3}
            aria-describedby={`${id}-emails-hint`}
            value={emails}
            onChange={(event) => setEmails(event.target.value)}
            disabled={busy}
          />
        </div>
        <div className="govuk-form-group">
          <label className="govuk-label" htmlFor={`${id}-role`}>
            Role
          </label>
          <select
            id={`${id}-role`}
            className="govuk-select"
            value={role}
            onChange={(event) => setRole(event.target.value)}
            disabled={busy}
          >
            {MEMBER_ROLES.map((value) => (
              <option key={value} value={value}>
                {ROLE_LABELS[value]}
              </option>
            ))}
          </select>
        </div>
        <button type="submit" className="govuk-button" disabled={busy}>
          {busy ? 'Inviting…' : 'Invite'}
        </button>
      </fieldset>
    </form>
  );
}
